import React, { useMemo } from 'react';
import { Box, Button, Container, Divider, Grid, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { OrderButtons } from '../PaymentSummary/OrderButtons';
import CustomContentPanel from '../CustomContentPanel';
import { useTransactionDetailPageStyle } from '../TransactionPage/TransactionDetailsPage/TransactionDetailsPage.style';
import { CloudTransactionResponse } from '../../transaction-handling/cloud-transaction';
import { SpiCloudPairing } from '../../redux/reducers/PairingSlice/interfaces';
import {
  PATH_CASH_OUT,
  PATH_PURCHASE,
  PATH_REFUND,
  PATH_TRANSACTIONS,
} from '../../definitions/constants/routerConfigs';
import { CloudOrderSummary } from '../CloudOrderSummary/CloudOrderSummary';
import { CloudOrderPairingInfo } from '../CloudOrderPairingInfo/CloudOrderPairingInfo';
import { ReactComponent as WarningIcon } from '../../images/WarningIcon.svg';
import { CloudPaymentDetails } from '../CloudPaymentDetails/CloudPaymentDetails';

interface Props {
  pairing?: SpiCloudPairing;
  transactionResponse?: CloudTransactionResponse;
  fromTransactionHistory?: boolean;
  splitTransaction?: {
    splitAmount: number;
    outstandingAmount: number;
    onClickNext: () => void;
    splitMode: string;
    numberOfSplits: number;
    splitIndex: number;
  };
}

export function CloudPaymentSummary({ pairing, transactionResponse, fromTransactionHistory, splitTransaction }: Props) {
  const classes = useTransactionDetailPageStyle();
  const history = useHistory();

  const isUnknown = useMemo(
    () =>
      transactionResponse?.data?.status !== 'FINALISED' ||
      transactionResponse?.data?.result_financial_status === 'UNKNOWN',
    [transactionResponse]
  );

  const typePath = useMemo(() => {
    if (!transactionResponse?.data?.result_amounts) {
      return PATH_PURCHASE;
    }

    const { cashout_amount, purchase_amount, refund_amount } = transactionResponse.data.result_amounts;

    if (refund_amount) {
      return PATH_REFUND;
    }

    if (cashout_amount && !purchase_amount) {
      return PATH_CASH_OUT;
    }

    return PATH_PURCHASE;
  }, [transactionResponse?.data?.result_amounts]);

  const goToTransactions = () => {
    history.push(PATH_TRANSACTIONS);
  };

  return (
    <Container className={classes.container} maxWidth="md">
      {fromTransactionHistory && (
        <Button className={classes.backLink} onClick={() => goToTransactions()}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="#393F73" />
          </svg>
          Back to Transaction history
        </Button>
      )}
      <Grid container spacing={1} className={classes.gridContainer}>
        <Grid item xs={6} className={classes.gridItem}>
          <Box className={classes.root}>
            <Box flexGrow="2" className={classes.roots}>
              {isUnknown && (
                <Box>
                  <WarningIcon />
                  <Typography variant="h6" component="h1">
                    Unknown
                  </Typography>
                </Box>
              )}
              <Typography variant="h6" component="h1">
                {transactionResponse?.data?.message}
              </Typography>
              <Divider className={classes.divider} variant="middle" />
              <CloudOrderPairingInfo pairing={pairing} />
              <Divider className={classes.divider} variant="middle" />
              <CloudOrderSummary resultAmounts={transactionResponse?.data?.result_amounts} />
              {fromTransactionHistory ? null : <OrderButtons typePath={typePath} splitTransaction={splitTransaction} />}
              <Divider className={classes.divider} variant="middle" />
              <CloudPaymentDetails resultCardDetails={transactionResponse?.data?.result_card_details} />
            </Box>
          </Box>
        </Grid>
        <Grid className={classes.gridItem}>
          <CustomContentPanel
            title="Receipt"
            css={classes.receiptBoxWrapper}
            isCopiable
            content={transactionResponse?.data?.merchant_receipt}
          >
            <pre>{transactionResponse?.data?.merchant_receipt}</pre>
          </CustomContentPanel>
        </Grid>
      </Grid>
    </Container>
  );
}
